/* FILE: BasicSettings.tsx */
/* BLOCK 1 START - Import statements */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { Button } from '../../ui/Button';
import { cn } from '../../utils/cn';
import { buildPatchFromChange } from '../../utils/patchMap';
import { useAuth } from '../../contexts/AuthContext';
/* BLOCK 1 END */

/* BLOCK 2 START - Interface definition */
interface BasicSettingsProps {
  botId?: string;
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
}
/* BLOCK 2 END */

/* BLOCK 3 START - Main component */
export const BasicSettings: React.FC<BasicSettingsProps> = ({
  botId,
  name,
  setName,
  description,
  setDescription,
}) => {
  console.log('BLOCK 3 - BasicSettings.tsx: Component initialized with props:', { botId, name, description });

  /* SUB-BLOCK 3.1 START - Hooks */
  const { user } = useAuth();
  console.log('SUB-BLOCK 3.1 - BasicSettings.tsx: Auth context user:', user);
  /* SUB-BLOCK 3.1 END */

  /* SUB-BLOCK 3.2 START - Name change handler */
  const handleNameChange = (value: string) => {
    console.log('SUB-BLOCK 3.2 - BasicSettings.tsx: Name changing from', name, 'to', value);
    setName(value);

    if (window.sendChange) {
      window.sendChange('name', value);
      console.log('SUB-BLOCK 3.2 - BasicSettings.tsx: sendChange called for name');
    }
  };
  /* SUB-BLOCK 3.2 END */

  /* SUB-BLOCK 3.3 START - Description change handler */
  const handleDescriptionChange = (value: string) => {
    console.log('SUB-BLOCK 3.3 - BasicSettings.tsx: Description changing from', description, 'to', value);
    setDescription(value);

    if (window.sendChange) {
      window.sendChange('description', value);
      console.log('SUB-BLOCK 3.3 - BasicSettings.tsx: sendChange called for description');
    }
  };
  /* SUB-BLOCK 3.3 END */

  /* SUB-BLOCK 3.4 START - JSX return */
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* SUB-BLOCK 3.4.1 START - Main container */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        
        {/* SUB-BLOCK 3.4.1.1 START - Header section */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Basic Settings</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Configure your bot's basic information</p>
          </div>
          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
            <Info className="h-6 w-6 text-blue-500" />
          </div>
        </div>
        {/* SUB-BLOCK 3.4.1.1 END */}

        {/* SUB-BLOCK 3.4.1.2 START - Form fields */}
        <div className="space-y-6">
          
          {/* SUB-BLOCK 3.4.1.2.1 START - Bot name field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Bot Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Enter bot name"
              className={cn(
                "w-full px-4 py-2 rounded-lg border",
                "bg-white dark:bg-gray-700",
                "border-gray-300 dark:border-gray-600",
                "text-gray-900 dark:text-white",
                "focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                "placeholder-gray-500 dark:placeholder-gray-400"
              )}
            />
          </div>
          {/* SUB-BLOCK 3.4.1.2.1 END */}

          {/* SUB-BLOCK 3.4.1.2.2 START - Description field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder="Enter bot description (optional)"
              rows={3}
              className={cn(
                "w-full px-4 py-2 rounded-lg border",
                "bg-white dark:bg-gray-700",
                "border-gray-300 dark:border-gray-600",
                "text-gray-900 dark:text-white",
                "focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                "placeholder-gray-500 dark:placeholder-gray-400",
                "resize-none"
              )}
            />
          </div>
          {/* SUB-BLOCK 3.4.1.2.2 END */}

        </div>
        {/* SUB-BLOCK 3.4.1.2 END */}
      </div>
      {/* SUB-BLOCK 3.4.1 END */}
      {/* SUB-BLOCK 3.4 END */}
    </motion.div>
  );
};
/* BLOCK 3 END */